import { 
  ProfileMaterialConfigurationRequest, 
  NodeMaterialConfigurationDto, 
  MaterialTypeConfiguration, 
  MaterialConfigurationType
} from "@api/apiClient";
import { TreeGridNode } from "../types/ProfileMaterialConfiguration";

// Transform tree grid data to JSON configuration format for API
export const transformTreeDataToJSON = (treeData: TreeGridNode[], profileId: string): ProfileMaterialConfigurationRequest => {
  const nodeConfigurations: NodeMaterialConfigurationDto[] = treeData.map(node => {
    const materialTypes: MaterialTypeConfiguration[] = [];
    
    // Add rebar material if present
    if (node.rebarMaterialId) {
      const rebarConfig = new MaterialTypeConfiguration();
      rebarConfig.type = MaterialConfigurationType.Rebar;
      rebarConfig.co2MaterialTypeId = node.rebarMaterialId.toString();
      rebarConfig.percentage = node.rebarPercentage;
      materialTypes.push(rebarConfig);
    }
    
    // Add non-modelled material if present
    if (node.nonModelledMaterialId) {
      const nonModelledConfig = new MaterialTypeConfiguration();
      nonModelledConfig.type = MaterialConfigurationType.NonModelled;
      nonModelledConfig.co2MaterialTypeId = node.nonModelledMaterialId.toString();
      nonModelledConfig.percentage = node.nonModelledPercentage;
      materialTypes.push(nonModelledConfig);
    }
    
    // Add auto-assign material if present
    if (node.autoAssignMaterialId) {
      const autoAssignConfig = new MaterialTypeConfiguration();
      autoAssignConfig.type = MaterialConfigurationType.AutoAssign;
      autoAssignConfig.co2MaterialTypeId = node.autoAssignMaterialId.toString();
      materialTypes.push(autoAssignConfig);
    }
    
    const nodeConfig = new NodeMaterialConfigurationDto();
    nodeConfig.nodeHierarchyId = node.id;
    nodeConfig.materialTypes = materialTypes;
    
    return nodeConfig;
  }); // Only include nodes with materials

  const request = new ProfileMaterialConfigurationRequest();
  request.profileId = profileId;
  request.nodeConfigurations = nodeConfigurations;
  
  return request;
};

// Transform JSON configuration to tree grid data format
export const transformJSONToTreeData = (
  jsonConfig: ProfileMaterialConfigurationRequest, 
  nodeHierarchyData: any[]
): TreeGridNode[] => {
  // Create a map of node configurations by node hierarchy ID
  const configMap = new Map<number, NodeMaterialConfigurationDto>();
  jsonConfig.nodeConfigurations?.forEach(config => {
    configMap.set(config.nodeHierarchyId as number, config);
  });

  // Transform node hierarchy data with material configuration
  return nodeHierarchyData.map(node => {
    const config = configMap.get(node.id);
    
    // Initialize default values
    let rebarMaterialId: string | undefined;
    let rebarPercentage: number | undefined;
    let nonModelledMaterialId: string | undefined;
    let nonModelledPercentage: number | undefined;
    let autoAssignMaterialId: string | undefined;
    // Extract material data from configuration
    if (config?.materialTypes) {
      const rebarMaterials = config.materialTypes.filter(m => m.type === MaterialConfigurationType.Rebar);
      const nonModelledMaterials = config.materialTypes.filter(m => m.type === MaterialConfigurationType.NonModelled);
      const autoAssignMaterials = config.materialTypes.filter(m => m.type === MaterialConfigurationType.AutoAssign);

      // Process rebar materials
      if (rebarMaterials.length > 0) {
        rebarMaterialId = rebarMaterials[0].co2MaterialTypeId || '0' 
        rebarPercentage = rebarMaterials[0].percentage;
      }

      // Process non-modelled materials
      if (nonModelledMaterials.length > 0) {
        nonModelledMaterialId = (nonModelledMaterials[0].co2MaterialTypeId || '0');
        nonModelledPercentage = nonModelledMaterials[0].percentage;
      }

      // Process auto-assign materials
      if (autoAssignMaterials.length > 0) {
        autoAssignMaterialId = (autoAssignMaterials[0].co2MaterialTypeId || '0');
      }
    }

    return {
      id: node.id,
      name: node.name,
      nodeType: node.nodeType,
      parentId: node.parentId,
      level: (node as any).level || 0, // Use any for level since it might not be in DTO
      hasChildren: (node as any).hasChildren || false, // Use any for hasChildren since it might not be in DTO
      rebarPercentage: rebarPercentage || 0,
      rebarMaterialId,
      rebarMaterialName: undefined, // Will be populated from material types lookup
      nonModelledMaterialId,
      nonModelledMaterialName: undefined, // Will be populated from material types lookup
      nonModelledPercentage: nonModelledPercentage || 0,
      autoAssignMaterialId,
      autoAssignMaterialName: undefined, // Will be populated from material types lookup
      changedFields: [] // Reset changed fields when loading from server
    };
  });
};

// Populate material names from CO2 material types
export const populateMaterialNames = (treeData: TreeGridNode[], co2MaterialTypes: any[]): TreeGridNode[] => {
  if (!co2MaterialTypes || co2MaterialTypes.length === 0) {
    return treeData;
  }
  console.info("Populating material names with CO2 material types:", co2MaterialTypes);
  return treeData.map(node => {
    const rebarMaterial = co2MaterialTypes.find(type => type.co2MaterialTypeId === node.rebarMaterialId);
    const nonModelledMaterial = co2MaterialTypes.find(type => type.co2MaterialTypeId === node.nonModelledMaterialId);
    const autoAssignMaterial = co2MaterialTypes.find(type => type.co2MaterialTypeId === node.autoAssignMaterialId);
    if(rebarMaterial!==undefined||node.id==7){
      console.info(node)
      console.info(`Node ${node.id} - Rebar Material:`, rebarMaterial);
    }
    return {
      ...node,
      rebarMaterialName: rebarMaterial?.name || undefined,
      nonModelledMaterialName: nonModelledMaterial?.name || undefined,
      autoAssignMaterialName: autoAssignMaterial?.name || undefined
    };
  });
};